import { useSmartQuery, useSmartMutation } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { organizationSetupApi } from "./api";
import {
  UpdateTenantRequest,
  InviteUserRequest,
  UpdateUserRequest,
} from "./types";
import { useTenantId } from "@shared/selectors/globalSelectors";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { queryClient } from "@shared/api/queries/queryClient";
import { PAGE_SIZE } from "@shared/utils/constants";

// 🏢 Tenant Detail Hooks
export const useTenantDetail = () => {
  const tenantId = useTenantId();
  const key = QueryKeyFactory.module.onboarding.tenantDetail();
  return useSmartQuery(key as unknown[], () =>
    organizationSetupApi.getTenantDetail(tenantId),
   {
     enabled: !!tenantId,
  }
  );
};

export const useUpdateTenantDetail = () => {
  const { toast } = useToast();
  const tenantId = useTenantId();

  const tenantKey = QueryKeyFactory.module.onboarding.tenantDetail();
  const orgSetupKey = QueryKeyFactory.module.onboarding.orgSetup();

  return useSmartMutation(
    (data: UpdateTenantRequest) =>
      organizationSetupApi.updateTenantDetail(data, tenantId),
    {
      onSuccess: () => {
        // ✅ Ensure both queries are invalidated
        queryClient.invalidateQueries({ queryKey: tenantKey });
        console.log("🔄 Invalidated:", tenantKey);

        queryClient.invalidateQueries({ queryKey: orgSetupKey });
        console.log("🔄 Invalidated:", orgSetupKey);

        toast({
          title: "Success",
          description: "Organization details updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message || "Failed to update organization details",
          variant: "destructive",
        });
      },
    }
  );
};



// 🌐 Choices Hooks
export const useDateFormats = () => {
  const key = QueryKeyFactory.module.onboarding.dateFormats();
  return useSmartQuery(key as unknown[], organizationSetupApi.getDateFormats);
};

export const useCurrencies = () => {
  const key = QueryKeyFactory.module.onboarding.currencies();
  return useSmartQuery(key as unknown[], organizationSetupApi.getCurrencies);
};

export const useTimezones = () => {
  const key = QueryKeyFactory.module.onboarding.timezones();
  return useSmartQuery(key as unknown[], organizationSetupApi.getTimezones);
};

export const useDepartments = () => {
  const remoteUtilityId = useRemoteUtilityId();

  const key = QueryKeyFactory.module.onboarding.departments();

  return useSmartQuery(
    key as unknown[],
    () => organizationSetupApi.getDepartments(remoteUtilityId),
    {
      enabled: !!remoteUtilityId, // 👈 Only run when remoteUtilityId is truthy
    }
  );
};


// 👥 User Hooks
export const useUsers = (page: number = 1, limit: number = 50) => {
  const tenantId = useTenantId();
  const key = QueryKeyFactory.module.onboarding.users(page, limit);
  return useSmartQuery(key as unknown[], () =>
    organizationSetupApi.getUsers(tenantId, page, limit),
  {
     enabled: !!tenantId,
  }
  );
};

export const useInviteUser = () => {
  const { toast } = useToast();
  const usersKey = QueryKeyFactory.module.onboarding.users();

  return useSmartMutation(
    (data: any) => organizationSetupApi.inviteUser(data),
    {
      onSuccess: () => {
        toast({
          title: "User Invited",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message || "Failed to invite user",
          variant: "destructive",
        });
      },
      invalidateKeys: [usersKey as unknown[]],
    }
  );
};

export const useUpdateUser = () => {
  const { toast } = useToast();
  const tenantId = useTenantId();
  const usersKey = QueryKeyFactory.module.onboarding.users();

  return useSmartMutation(
    ({ userId, userData }: { userId: number; userData: UpdateUserRequest }) =>
      organizationSetupApi.updateUser(userId, userData, tenantId),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "User updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message || "Failed to update user",
          variant: "destructive",
        });
      },
      invalidateKeys: [usersKey as unknown[]],
    }
  );
};

export const useUpdateUserName = () => {
  const { toast } = useToast();
  const usersKey = QueryKeyFactory.module.onboarding.users();

  return useSmartMutation(
    ({ userId, payload }: { userId: number; payload: any }) =>
      organizationSetupApi.updateUserName(userId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.users(1, Number(PAGE_SIZE)),
        });
         toast({
           title: "Success",
          description: "User name updated successfully.",
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message || "Failed to update user",
          variant: "destructive",
        });
      },
      invalidateKeys: [usersKey as unknown[]],
    }
  );
};

export const toggleUser = () => {
  const { toast } = useToast();
  const usersKey = QueryKeyFactory.module.onboarding.users();

  return useSmartMutation(
    ({ userId, payload }: { userId: number; payload: any }) =>
      organizationSetupApi.toggleUser(userId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.users(1, Number(PAGE_SIZE)),
        });
          toast({
          title: "Success",
          description: "User status updated successfully",
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message || "Failed to update user",
          variant: "destructive",
        });
      },
      invalidateKeys: [usersKey as unknown[]],
    }
  );
};

export const useToggleUserStatus = () => {
  const { toast } = useToast();
  const usersKey = QueryKeyFactory.module.onboarding.users();

  return useSmartMutation(
    ({ userId, payload }: { userId: number; payload: any }) =>
      organizationSetupApi.toggleUser(userId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "User status updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message || "Failed to update user status",
          variant: "destructive",
        });
      },
      invalidateKeys: [usersKey as unknown[]],
    }
  );
};

export const useDeleteUser = () => {
  const { toast } = useToast();
  const usersKey = QueryKeyFactory.module.onboarding.users();

  return useSmartMutation(
    (userId: number) => organizationSetupApi.deleteUser(userId),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "User removed successfully",
        });
          queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.users(1, Number(PAGE_SIZE)),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message || "Failed to remove user",
          variant: "destructive",
        });
      },
      invalidateKeys: [usersKey as unknown[]],
    }
  );
};
